import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, API } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const razorpayOrderId = params.get("razorpay_order_id");
  const orderId = params.get("order_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired | timeout
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    // New unified orders flow (add-to-cart -> checkout form -> razorpay)
    if (orderId) {
      api
        .get(`/orders/${orderId}`)
        .then((r) => {
          setData({
            product_name:
              (r.data.items && r.data.items[0]?.name) || undefined,
            amount_total: r.data.total_paise,
            currency: r.data.currency,
            customer_email: r.data.customer?.email,
            item_count: (r.data.items || []).length,
          });
          setState(r.data.payment_status === "paid" ? "paid" : "failed");
        })
        .catch(() => setState("failed"));
      return;
    }

    // Legacy razorpay-single-product flow
    if (razorpayOrderId) {
      api
        .get(`/razorpay/order/${razorpayOrderId}`)
        .then((r) => {
          setData({
            product_name: r.data.product_name,
            amount_total: r.data.amount,
            currency: r.data.currency,
          });
          setState(r.data.payment_status === "paid" ? "paid" : "failed");
        })
        .catch(() => setState("failed"));
      return;
    }

    // Legacy stripe flow — poll
    if (!sessionId) {
      setState("failed");
      return;
    }

    let cancelled = false;
    const MAX = 10;
    const INTERVAL = 2000;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX) {
        setState("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        setState("failed");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, razorpayOrderId, orderId]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen">
      <Header />

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        {state === "polling" && (
          <div data-testid="status-polling">
            <Loader2 className="mx-auto animate-spin text-[#D4AF37]" strokeWidth={1.2} size={40} />
            <p className="mt-8 text-[11px] tracking-overline uppercase text-[#D4AF37] font-bold">
              Confirming your payment
            </p>
            <h1 className="mt-4 font-serif-display text-4xl md:text-5xl text-[#F4EAD9]">
              One moment…
            </h1>
            <p className="mt-4 text-sm text-[#B0A496]">
              We&apos;re checking with Stripe. This usually takes a few seconds.
            </p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="status-paid">
            <CheckCircle2 className="mx-auto text-[#4A5D23]" strokeWidth={1.2} size={48} />
            <p className="mt-8 text-[11px] tracking-overline uppercase text-[#D4AF37] font-bold">
              Order received
            </p>
            <h1 className="mt-4 font-serif-display text-5xl md:text-6xl text-[#F4EAD9] leading-none">
              Thank you<span className="italic">.</span>
            </h1>
            <p className="mt-6 text-base text-[#B0A496]">
              {data?.product_name
                ? `Your ${data.product_name} is being decanted.`
                : "Your fragrance is being decanted."}{" "}
              A confirmation will arrive in your inbox shortly.
            </p>
            {data && (
              <p data-testid="paid-amount" className="mt-3 text-sm tabular-nums text-[#D4AF37]">
                Paid {data.currency?.toUpperCase()} ₹
                {(data.amount_total / 100).toLocaleString("en-IN")}
              </p>
            )}
            <Link
              data-testid="back-home-cta"
              to="/shop"
              className="mt-12 inline-block bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase transition-colors font-bold"
            >
              Continue browsing
            </Link>
          </div>
        )}

        {(state === "failed" || state === "expired" || state === "timeout") && (
          <div data-testid="status-failed">
            <XCircle className="mx-auto text-[#8B0000]" strokeWidth={1.2} size={48} />
            <p className="mt-8 text-[11px] tracking-overline uppercase text-[#D4AF37] font-bold">
              Something didn&apos;t go through
            </p>
            <h1 className="mt-4 font-serif-display text-4xl md:text-5xl text-[#F4EAD9]">
              Payment not completed
            </h1>
            <p className="mt-4 text-sm text-[#B0A496]">
              {state === "expired"
                ? "Your checkout session expired."
                : state === "timeout"
                ? "We couldn't confirm your payment in time. Please check your email or try again."
                : "We couldn't process your payment."}
            </p>
            <Link
              data-testid="retry-cta"
              to="/shop"
              className="mt-12 inline-block border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase transition-colors font-bold"
            >
              Return to shop
            </Link>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default Success;
